import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import SchoolDiscoveryPanel, { useDiscoveryApi } from '../components/schools/SchoolDiscoveryPanel';
import { canFindSchoolAsCoach } from '../utils/roles';

export default function FindSchoolPage() {
    const { t } = useTranslation(['applyPage', 'common']);
    const statusConfig = useMemo(() => ({
        pending: { label: t('applyPage:statusPending'), color: '#e65100', bg: '#fff3e0' },
        approved: { label: t('applyPage:statusApproved'), color: '#2e7d32', bg: '#e8f5e9' },
        rejected: { label: t('applyPage:statusRejected'), color: '#c62828', bg: '#fce4ec' }
    }), [t]);
    const api = useDiscoveryApi();
    const [myApps, setMyApps] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [applyModal, setApplyModal] = useState(null);

    useEffect(() => {
        if (!canFindSchoolAsCoach()) return;
        loadMyApps();
    }, []);

    async function loadMyApps() {
        try {
            const res = await api.get('/application/my');
            setMyApps((res.data || []).filter(a => a.role === 'coach'));
        } catch {
            setMyApps([]);
        }
    }

    async function handleApply(schoolId) {
        setLoading(true);
        try {
            await api.post('/application', {
                schoolId,
                role: 'coach',
                message: message.trim() || null
            });
            alert(t('applyPage:submitSuccessCoach'));
            setMessage('');
            setApplyModal(null);
            loadMyApps();
        } catch (err) {
            alert(err.response?.data?.message || t('applyPage:submitError'));
        }
        setLoading(false);
    }

    if (!canFindSchoolAsCoach()) {
        return (
            <OwnerPageLayout title={t('applyPage:findTitleShort')} maxWidth={560}>
                <p style={{ textAlign: 'center' }}>{t('applyPage:coachOnly')}</p>
            </OwnerPageLayout>
        );
    }

    const myAppsBlock = myApps.length > 0 ? (
        <div style={{ marginBottom: 24, background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e8ecf4' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>{t('applyPage:myApplications')}</h3>
            {myApps.map(app => {
                const st = statusConfig[app.status] || statusConfig.pending;
                return (
                    <div key={app.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 14 }}>
                        <span>{app.schoolName}</span>
                        <span style={{ color: st.color, fontWeight: 600 }}>{st.label}</span>
                    </div>
                );
            })}
        </div>
    ) : null;

    return (
        <>
            <OwnerPageLayout
                title={t('applyPage:findTitle')}
                subtitle={t('applyPage:findSubtitle')}
                maxWidth={960}
            >
                <SchoolDiscoveryPanel
                    api={api}
                    onApply={(s) => setApplyModal(s)}
                    applyLabel={t('applyPage:btnApply')}
                    showMyApps={myAppsBlock}
                />
            </OwnerPageLayout>

            {applyModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                }} onClick={() => setApplyModal(null)}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ margin: '0 0 8px' }}>{t('applyPage:modalTitle', { name: applyModal.name })}</h3>
                        <p style={{ color: '#666', fontSize: 14, margin: '0 0 16px' }}>{t('applyPage:modalMessageHint')}</p>
                        <textarea
                            value={message}
                            onChange={e => setMessage(e.target.value)}
                            rows={4}
                            maxLength={500}
                            style={{ width: '100%', padding: 12, borderRadius: 10, border: '1.5px solid var(--sp-gray-200)', boxSizing: 'border-box', marginBottom: 16 }}
                        />
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button type="button" onClick={() => setApplyModal(null)} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--sp-gray-200)', background: '#fff', cursor: 'pointer' }}>
                                {t('common:cancel')}
                            </button>
                            <button
                                type="button"
                                disabled={loading}
                                onClick={() => handleApply(applyModal.id)}
                                style={{ flex: 1, padding: 12, borderRadius: 10, border: 'none', background: '#EB3333', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {loading ? '…' : t('applyPage:send')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
